import React, { ChangeEventHandler, KeyboardEventHandler, ReactNode, forwardRef } from 'react';
import styled from 'styled-components';

import BasicText, { ITextVariants } from '@ui/atoms/BasicText';
import BodyText from '@ui/atoms/BodyText';

import { ColorTypes, RadiusTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

interface ITextBoxProps {
	border?: boolean;
	borderColor?: ColorTypes;
	borderRadius?: RadiusTypes;
	disabled?: boolean;
	error?: boolean;
}

interface ITextInputProps extends ITextBoxProps {
	children?: ReactNode;
	className?: string;
	fullWidth?: boolean;
	label?: string;
	subLabel?: string;
	labelVariant?: ITextVariants;
	name?: string;
	onChange?: ChangeEventHandler<HTMLInputElement>;
	onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
	onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
	placeholder?: string;
	type?: 'text' | 'password' | 'email' | 'tel' | 'date' | 'month' | 'number';
	value?: string;
	readOnly?: boolean;
	errorLabel?: string;
	maxLength?: number;
	startDecorator?: ReactNode;
	endDecorator?: ReactNode;
}

const TextBox = styled.div<ITextBoxProps>`
	border-radius: ${({ borderRadius = 'none' }) => theme.radius[borderRadius]};
	border: ${({ border = true, error, disabled }) =>
		!border || error
			? `1px solid ${theme.colors.transparent}`
			: disabled
			? `1px solid ${theme.colors.darkGray}`
			: `1px solid ${theme.colors.black}`};
	outline: ${({ error }) =>
		error ? `1px solid ${theme.tints.darken40(theme.colors.red)}` : 'none'};
	background-color: ${({ disabled = false }) =>
		disabled ? theme.colors.lightGray : theme.colors.white};

	align-items: center;
	display: flex;
	flex-direction: row;
	gap: ${theme.spacing.xs};
	overflow: hidden;
	position: relative;

	&:focus-within {
		border: 1px solid ${theme.colors.transparent};
		border-radius: ${({ borderRadius = 'none' }) => theme.radius[borderRadius]};
		outline: ${({ borderColor = 'cyan', error }) =>
			`2px solid ${error ? theme.tints.darken40(theme.colors.red) : theme.colors[borderColor]}`};
	}
`;

const TextLabel = styled.div<{ fullWidth?: boolean }>`
	display: flex;
	flex-flow: column;
	width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};

	${BodyText}, ${BasicText} {
		margin-bottom: ${({ theme: components }) => components.basicText.labelMarginBottom};
	}
`;

const ErrorBox = styled.span`
	color: ${({ theme: components }) => components.textInput.errorColor};
	font-size: ${({ theme: components }) => components.textInput.errorFontSize};
	line-height: ${({ theme: components }) => components.textInput.errorLineHeight};
	margin-top: 0.8rem;
`;

const BaseWrapper = styled.div`
	display: flex;
	flex-flow: column;
`;

const TextInput = forwardRef<HTMLInputElement, ITextInputProps>(
	(
		{
			border,
			borderColor,
			borderRadius,
			children,
			className,
			disabled = false,
			endDecorator,
			error = false,
			errorLabel,
			fullWidth = true,
			label = '',
			labelVariant = 'field',
			maxLength,
			name = '',
			onChange = () => {},
			onKeyDown = () => {},
			onKeyUp = () => {},
			placeholder,
			readOnly = false,
			startDecorator,
			subLabel = '',
			type = 'text',
			value,
			...restProps
		},
		ref
	) => {
		const input = (
			<input
				ref={ref}
				type={type}
				name={name}
				placeholder={placeholder}
				onChange={onChange}
				onKeyDown={onKeyDown}
				onKeyUp={onKeyUp}
				disabled={disabled}
				value={value}
				id={name}
				readOnly={readOnly}
				aria-label={name}
				maxLength={maxLength}
				{...restProps}
			/>
		);

		const textInput = (
			<TextBox
				className={className}
				border={border}
				borderColor={borderColor}
				borderRadius={borderRadius}
				disabled={disabled}
				error={error}>
				{startDecorator}
				{input}
				{endDecorator}
				{children}
			</TextBox>
		);

		return label ? (
			<TextLabel fullWidth={fullWidth}>
				<BasicText variant={labelVariant}>
					<label htmlFor={name}>{label}</label>
				</BasicText>
				{subLabel && <BodyText size="sm">{subLabel}</BodyText>}
				{textInput}
				{errorLabel && <ErrorBox>{errorLabel}</ErrorBox>}
			</TextLabel>
		) : (
			<BaseWrapper>
				{textInput}
				{errorLabel && <ErrorBox>{errorLabel}</ErrorBox>}
			</BaseWrapper>
		);
	}
);

export interface IStyledTextInputProps extends ITextInputProps {
	fullWidth?: boolean;
	autoComplete?: string;
}

TextInput.displayName = 'TextInput';
export default styled(TextInput)<IStyledTextInputProps>`
	input {
		border: none;
		outline: none;
		padding: 2rem 1rem;
		font-family: Chalet-LondonSixty, sans-serif;
		font-size: ${({ theme: components }) => components.basicText.body.fontSize.mobile};
		width: ${({ fullWidth = true }) => (fullWidth ? '100%' : 'auto')};

		@media ${theme.responsive.breakpoints.tablet} {
			font-size: ${({ theme: components }) => components.basicText.body.fontSize.tablet};
		}

		&:disabled {
			color: ${theme.tints.lighten70(theme.colors.black)};
		}

		&::placeholder {
			color: ${theme.colors.placeholderGray};
		}

		width: ${({ fullWidth = true }) => (fullWidth ? '100%' : 'auto')};

		&[type='date'] {
			-webkit-appearance: textfield;
			-moz-appearance: textfield;
			color: ${theme.colors.black};
			cursor: text;
		}
		&[type='number'] {
			&::-webkit-outer-spin-button,
			&::-webkit-inner-spin-button {
				-webkit-appearance: none;
				margin: 0;
			}
			-moz-appearance: textfield;
		}
	}
`;
